import {ProjectId, ProjectStagePhaseIdPath} from "../../models/projects/Project";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {TimesheetEntryProject} from "../../models/timesheet/Timesheet";
import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {UserViewSettings, UserViewSettingsProp} from "../../models/common/ViewSettings";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ajax} from "rxjs/ajax";
import {catchError, map, mergeMap, switchMap} from "rxjs/operators";
import {eventNotificationError, eventNotificationShow} from "../common/EventNotificationActions";
import {getUrlParamsFromViewSettings} from "../../config/RouteConfig";
import {projectFilterName} from "../../components/common/data-filters/ProjectFilterInput";
import {projectStageFilterName} from "../../components/common/data-filters/ProjectStageFilterInput";
import {projectStagePhaseFilterName} from "../../components/common/data-filters/ProjectStagePhaseFilterInput";
import {
    constructPhaseFilterActive,
    constructProjectFilterActive,
    constructStageFilterActive,
    DATA_FILTER_PREFIX
} from "../../models/common/DataFilter";
import {FileData} from "../../models/common/FileData";
import {ExportTypes} from "../../models/common/Export";
import {currentLanguageFromStore} from "../../config/LanguageConfig";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import { downloadFileHelper } from "../../helpers/FileHelpers";

/**
 * introduce new type to have projectId required, but make stage and phase id optional
 */
type ProjectRequiredPhaseIdPath = ProjectId & Partial< Omit< ProjectStagePhaseIdPath, 'projectId' >>

export interface FetchProjectTimesheetAction extends ProjectRequiredPhaseIdPath, UserViewSettingsProp, ReduxAction<typeof FETCH_PROJECT_TIMESHEET> {

}
export interface FetchProjectTimesheetSuccessAction extends ProjectRequiredPhaseIdPath, ReduxAction<typeof FETCH_PROJECT_TIMESHEET_SUCCESS> {
    payload: ApiReturnType< TimesheetEntryProject[] >
}
export interface FetchProjectTimesheetErrorAction extends ProjectRequiredPhaseIdPath, ErrorAction<typeof FETCH_PROJECT_TIMESHEET_ERROR> {

}


export type FetchProjectTimesheetActionTypes = FetchProjectTimesheetAction | FetchProjectTimesheetSuccessAction | FetchProjectTimesheetErrorAction

export const FETCH_PROJECT_TIMESHEET = "FETCH_PROJECT_TIMESHEET";
export const FETCH_PROJECT_TIMESHEET_SUCCESS = "FETCH_PROJECT_TIMESHEET_SUCCESS";
export const FETCH_PROJECT_TIMESHEET_ERROR = "FETCH_PROJECT_TIMESHEET_ERROR";

export function fetchProjectTimesheet(
    projectPhaseIdPath: ProjectRequiredPhaseIdPath,
    userViewSettings: UserViewSettings
): FetchProjectTimesheetAction {
    return { type: FETCH_PROJECT_TIMESHEET, ...projectPhaseIdPath, userViewSettings };
}

export function fetchProjectTimesheetSuccess(
    projectPhaseIdPath: ProjectRequiredPhaseIdPath,
    payload: ApiReturnType< TimesheetEntryProject[] >
): FetchProjectTimesheetSuccessAction {
    return { type: FETCH_PROJECT_TIMESHEET_SUCCESS, ...projectPhaseIdPath, payload };
}

export function fetchProjectTimesheetError(
    projectPhaseIdPath: ProjectRequiredPhaseIdPath,
    error: any
): FetchProjectTimesheetErrorAction {
    return { type:FETCH_PROJECT_TIMESHEET_ERROR, ...projectPhaseIdPath, error };
}

export function fetchProjectTimesheetEpic(
    action$: Observable< FetchProjectTimesheetAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_TIMESHEET ),
        mergeMap( action => {

            const viewSettings = store$.value.ViewSettingsReducer
                .userViewSettings[ action.userViewSettings.viewIdentifier.rawValue ]
            ?? action.userViewSettings;

            //add filters project filters
            const userViewSettings = {
                ...viewSettings,
                activeFilters: [
                    ...viewSettings.activeFilters.filter( _filter =>
                        _filter.identifier != DATA_FILTER_PREFIX + projectFilterName
                        && _filter.identifier != DATA_FILTER_PREFIX + projectStageFilterName
                        && _filter.identifier != DATA_FILTER_PREFIX + projectStagePhaseFilterName
                    ),
                    ...[
                        constructProjectFilterActive( action.projectId ),
                        ( typeof action.projectStageId !== 'undefined' && action.projectStageId.rawValue > 0 )
                            ? constructStageFilterActive( action.projectStageId )
                            : null,
                        ( typeof action.phaseId !== 'undefined' && action.phaseId.rawValue > 0 )
                            ? constructPhaseFilterActive( action.phaseId )
                            : null,
                    ].filter( _filter => _filter !== null )
                ]
            };

            let path = `${ APP_CONFIG.baseWeb }projects/${ action.projectId.rawValue }`;

            if( (action.projectStageId?.rawValue ?? 0 ) > 0 ) {
                path += "/stages/"+ action.projectStageId!.rawValue;
            }
            if( ( action.phaseId?.rawValue ?? 0 ) > 0 ) {
                path += "/phases/"+ action.phaseId!.rawValue;
            }

            // @ts-ignore
            path += "/timesheet/"+getUrlParamsFromViewSettings( userViewSettings );

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                    map( _response => fetchProjectTimesheetSuccess(
                        { projectId: action.projectId, projectStageId: action.projectStageId, phaseId: action.phaseId },
                        _response.response as ApiReturnType< TimesheetEntryProject[] >
                    ) ),
                    catchError( _error => of(
                        fetchProjectTimesheetError(
                            { projectId: action.projectId, projectStageId: action.projectStageId, phaseId: action.phaseId },
                            _error
                        ),
                        eventNotificationError( _error, store$, "project-timesheet", "fetchProjectTimesheet" )
                    ) )
                );
        } )
    )
}

/**
 * export
 */
export interface FetchProjectTimesheetReportExportAction extends ProjectRequiredPhaseIdPath, UserViewSettingsProp, ReduxAction<typeof FETCH_PROJECT_TIMESHEET_REPORT_EXPORT > {
    fileData: FileData
    exportTypes: ExportTypes
}
export interface FetchProjectTimesheetReportExportSuccessAction extends ReduxAction<typeof FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_SUCCESS> {
}
export interface FetchProjectTimesheetReportExportErrorAction extends ErrorAction<typeof FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_ERROR> {
}

export type FetchProjectTimesheetReportExportActionTypes = FetchProjectTimesheetReportExportAction
    | FetchProjectTimesheetReportExportSuccessAction
    | FetchProjectTimesheetReportExportErrorAction

export const FETCH_PROJECT_TIMESHEET_REPORT_EXPORT = "FETCH_PROJECT_TIMESHEET_REPORT_EXPORT";
export const FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_SUCCESS = "FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_SUCCESS";
export const FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_ERROR = "FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_ERROR";

export function fetchProjectTimesheetReportExport(
    projectPhaseIdPath: ProjectRequiredPhaseIdPath,
    exportTypes: ExportTypes,
    userViewSettings: UserViewSettings,
    fileData: FileData
): FetchProjectTimesheetReportExportAction {
    return {
        ...projectPhaseIdPath,
        type: FETCH_PROJECT_TIMESHEET_REPORT_EXPORT,
        userViewSettings,
        exportTypes,
        fileData
    };
}
export function fetchProjectTimesheetReportExportSuccess(): FetchProjectTimesheetReportExportSuccessAction {
    return { type: FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_SUCCESS };
}
export function fetchProjectTimesheetReportExportError(
    error: Error
): FetchProjectTimesheetReportExportErrorAction {
    return {type: FETCH_PROJECT_TIMESHEET_REPORT_EXPORT_ERROR, error };
}

export function fetchProjectTimesheetReportExportEpic (
    action$: Observable< FetchProjectTimesheetReportExportAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_TIMESHEET_REPORT_EXPORT ),
        switchMap( action => {

            const language = currentLanguageFromStore( store$ );

            const viewSettings = store$.value.ViewSettingsReducer
                    .userViewSettings[ action.userViewSettings.viewIdentifier.rawValue ]
                ?? action.userViewSettings;

            //add filters project filters
            const userViewSettings = {
                ...viewSettings,
                activeFilters: [
                    ...viewSettings.activeFilters.filter( _filter =>
                        _filter.identifier != DATA_FILTER_PREFIX + projectFilterName
                        && _filter.identifier != DATA_FILTER_PREFIX + projectStageFilterName
                        && _filter.identifier != DATA_FILTER_PREFIX + projectStagePhaseFilterName
                    ),
                    ...[
                        constructProjectFilterActive( action.projectId ),
                        ( typeof action.projectStageId !== 'undefined' && action.projectStageId.rawValue > 0 )
                            ? constructStageFilterActive( action.projectStageId )
                            : null,
                        ( typeof action.phaseId !== 'undefined' && action.phaseId.rawValue > 0 )
                            ? constructPhaseFilterActive( action.phaseId )
                            : null,
                    ].filter( _filter => _filter !== null )
                ]
            };

            let path = `${ APP_CONFIG.baseWeb }projects/${ action.projectId.rawValue }`;

            if( (action.projectStageId?.rawValue ?? 0 ) > 0 ) {
                path += "/stages/"+ action.projectStageId!.rawValue;
            }
            if( ( action.phaseId?.rawValue ?? 0 ) > 0 ) {
                path += "/phases/"+ action.phaseId!.rawValue;
            }

            // @ts-ignore
            path += `/timesheet/export/${ action.exportTypes }/`+getUrlParamsFromViewSettings( userViewSettings );

            return ajax({
                ...APP_CONFIG.getRequestObject(path),
                responseType: "blob",
                async: false
            }).pipe(
                map( response => {
                    //@ts-ignore
                    downloadFileHelper( response.response, action.fileData );

                    return fetchProjectTimesheetReportExportSuccess();
                } ),
                catchError( error => {

                    return of(
                        fetchProjectTimesheetReportExportError(error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            createErrorResponse(
                                language,
                                error.status ?? 200,
                                "",
                                "fetchProjectTimesheetReportExport"
                            )
                        )
                    )
                } )
            )
        })
    );
}